/**
 * inboxAccountApi — inbox calls that don't live in the frozen `services/api.ts`.
 *
 * Standalone module per the `kickoffApi.ts` / `eosL10Api.ts` precedent; fold
 * into the typed client at the next api.ts unlock.
 *
 * NOTE: this file previously held `setAutoCloseDupes`, the Entry 330 toggle for
 * closing personal-inbox copies of Help Desk conversations. Michael replaced
 * that design with the "In Help Desk" section (Q6) — closing a thread removes
 * it from view entirely, which defeats the oversight it was meant to give him —
 * so the toggle, its route and its setting are gone rather than left as a
 * second, competing behaviour for the same condition.
 */
import axios from 'axios';

const api = axios.create({ baseURL: '/api/inbox', withCredentials: true });

export interface HelpdeskSibling {
  ticketId: number;
  ticketStatus: string | null;
  /** The thread the ticket lives on — the SUPPORT thread when `inHelpdeskInbox`. */
  threadId: number;
  /**
   * True when that thread is in the support@ inbox, so /helpdesk?thread= can
   * open it. False for a ticket made from a teammate's PERSONAL copy (before
   * personal inboxes stopped auto-ticketing, 2026-09-16) — link the ticket itself.
   */
  inHelpdeskInbox?: boolean;
}

/**
 * The `access` block GET /inbox/threads/:id returns (2026-09-16). A teammate can
 * READ a personal email when it is client correspondence or a Help Desk
 * ticket's email, but only the owner can reply — a reply goes out from the
 * owner's address. Absent on an older server, so treat missing as "can reply".
 */
export interface ThreadAccess {
  canReply: boolean;
  isOwner: boolean;
  ownerName: string | null;
}

/** Why reply/forward is unavailable on this thread, or null when it isn't. */
export function replyUnavailableReason(access: ThreadAccess | null | undefined): string | null {
  if (!access || access.canReply !== false) return null;
  const who = access.ownerName || 'the inbox owner';
  return `Only ${who} can reply. This email is in their personal inbox, and a reply would go out from their address.`;
}

export const inboxAccountApi = {
  /**
   * Q5 — the Help Desk ticket for the SAME delivered email as this thread, or
   * null when the conversation exists only here.
   *
   * Matched on the RFC822 Message-ID, the one identifier that is byte-identical
   * in every mailbox's copy of a message. Gmail assigns a distinct thread id
   * per mailbox, so a message sent to support@ with you on Cc becomes two
   * unrelated thread rows — which is why the ordinary per-thread ticket lookup
   * finds nothing and the rail claims there is no ticket.
   */
  getHelpdeskSibling: (threadId: number): Promise<{ success: boolean; data: HelpdeskSibling | null }> =>
    api.get(`/threads/${threadId}/helpdesk-sibling`).then((r) => r.data),
};

export default inboxAccountApi;
